import React, { useMemo } from "react";
import {
  DocumentFormData,
  ReservationUpdateFormData,
} from "@/components/pages/reservations/guests/utils/reservationSchema";
import { CheckinStatus, CheckoutStatus } from "@/types/reservation";
import {
  FormField,
  NonEditableFormField,
} from "../../guests/components/form/FormFields";
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Request } from "@/types/request";
import { PreCheckStatus } from "@/components/pages/settlement/types/settlementBooking";
import { CountryField } from "../../guests/components/form/CountryField";
import { DocumentItem } from "../DocumentItem";
import AddDocumentModal from "../AddDocumentModal";
import { Plus, Upload } from "lucide-react";
import { GuestType } from "@/types/guest";
import { ReservationStatus } from "./ReservationStatus";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type GuestInfoProps = {
  reservation?: ReservationUpdateFormData & {
    serviceRequests: Request[];
    checkinStatus: CheckinStatus;
    checkoutStatus: CheckoutStatus;
    preCheckoutStatus: PreCheckStatus;
    dateOfBirth: string | null;
  };
  onPhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveDocument: (documentId: number) => void;
  onDocumentSubmit: (data: DocumentFormData) => Promise<void>;
  isEditing: boolean;
  register: UseFormRegister<ReservationUpdateFormData>;
  watch: UseFormWatch<ReservationUpdateFormData>;
  setValue: UseFormSetValue<ReservationUpdateFormData>;
  errors: FieldErrors<ReservationUpdateFormData>;
  guestNotes?: string[];
};

export const GuestInfo: React.FC<GuestInfoProps> = ({
  reservation,
  onPhoneChange,
  onRemoveDocument,
  onDocumentSubmit,
  isEditing,
  register,
  watch,
  setValue,
  errors,
  guestNotes = [],
}) => {
  const { t } = useTranslation();
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const phone = watch("phone");
  const email = watch("email");
  const selectedCountry = watch("language");
  const documents = watch("documents");
  const additionalGuests = watch("additionalGuests");

  const { adultGuests, childGuests } = useMemo(() => {
    const list = additionalGuests || [];
    type AdditionalGuest = (typeof list)[number];

    const result: {
      adultGuests: { index: number; guest: AdditionalGuest }[];
      childGuests: { index: number; guest: AdditionalGuest }[];
    } = { adultGuests: [], childGuests: [] };

    list.forEach((guest, index) => {
      if (guest.guestType === GuestType.ADULT) {
        result.adultGuests.push({ index, guest });
      } else if (guest.guestType === GuestType.CHILD) {
        result.childGuests.push({ index, guest });
      }
    });

    return result;
  }, [additionalGuests]);

  return (
    <>
      <ReservationStatus
        checkinStatus={reservation?.checkinStatus}
        checkoutStatus={reservation?.checkoutStatus}
      />

      <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2">
        {/* Main guest */}
        <div>
          <h2 className="text-violet mb-4 text-sm">
            {t("reservation.profile.mainGuest")}
          </h2>
          <div className="grid grid-cols-1 gap-x-2 gap-y-3 md:grid-cols-2">
            <FormField
              id="firstName"
              label={t("reservation.form.name")}
              type="text"
              required
              register={register}
              error={errors?.firstName?.message}
              placeholder={t("reservation.placeholders.enterFirstName")}
              isEdit={isEditing}
              value={firstName}
              className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              labelClassName="text-foreground"
              containerClassName="flex flex-col space-y-3"
            />

            <FormField
              id="lastName"
              label={t("reservation.form.surname")}
              type="text"
              required
              register={register}
              error={errors?.lastName?.message}
              placeholder={t("reservation.placeholders.enterSurname")}
              isEdit={isEditing}
              value={lastName}
              className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              labelClassName="text-foreground"
              containerClassName="flex flex-col space-y-3"
            />

            <FormField
              id="phone"
              label={t("reservation.form.phone")}
              type="text"
              required
              register={register}
              error={errors?.phone?.message}
              onChange={onPhoneChange}
              placeholder={t("reservation.placeholders.enterPhone")}
              isEdit={isEditing}
              value={phone}
              isCopy={true}
              className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              labelClassName="text-foreground"
              containerClassName="flex flex-col space-y-3"
              buttonClassName="!p-0"
            />

            <FormField
              id="email"
              label={t("reservation.form.email")}
              type="email"
              required
              register={register}
              error={errors?.email?.message}
              placeholder={t("reservation.placeholders.enterEmail")}
              isEdit={isEditing}
              value={email}
              isCopy={true}
              className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              labelClassName="text-foreground"
              containerClassName="flex flex-col space-y-3"
              buttonClassName="!p-0"
            />

            <CountryField
              className="md:col-span-1"
              selectedCountry={selectedCountry ?? ""}
              onSelect={(code) => setValue("language", code)}
              error={errors.language?.message}
              isEdit={isEditing}
              labelClassName="text-foreground"
              contentClassName={`h-9 mt-3 px-3 py-1 items-center rounded-lg !border ${errors.language?.message ? "!border-red-500" : "!border-neutral-200"} !text-muted-foreground !text-sm`}
            />

            <NonEditableFormField
              value={
                reservation?.dateOfBirth
                  ? format(reservation.dateOfBirth, "dd.MM.yyyy")
                  : t("reservation.form.dateOfBirth")
              }
              label={t("reservation.form.dateOfBirth")}
              isCopy={false}
              labelClassName="text-foreground"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              containerClassName="flex-1"
            />
          </div>

          <div className="mt-4">
            {(documents && documents.length > 0) ||
              (isEditing && (
                <label className="text-foreground mb-2 block text-sm font-medium">
                  {t("reservation.form.uploadedDocuments")}
                </label>
              ))}
            <div className={"col-span-2 flex flex-row gap-2"}>
              {documents?.map((document, index) => (
                <DocumentItem
                  className={"max-h-[100px] min-h-full"}
                  key={index}
                  id={index}
                  name={document?.file?.name || ""}
                  remove={(id: number) => onRemoveDocument(id)}
                  fileId={document.fileId}
                  type={document.type}
                />
              ))}
              {isEditing && (
                <AddDocumentModal
                  trigger={
                    <div className={"flex items-end"}>
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-neutral-200">
                        <Upload className="text-foreground h-4 w-4" />
                      </div>
                      <Plus className="absolute -right-1 -bottom-1 h-2.5 w-2.5 rounded-full bg-black p-0 text-white hover:bg-gray-800" />
                    </div>
                  }
                  onSubmit={onDocumentSubmit}
                />
              )}
            </div>
          </div>
        </div>

        {/* Notes saved on the guest profile are also relevant from the reservation. */}
        <div className="min-w-0 md:border-l md:border-neutral-200 md:pl-4">
          <h2 className="text-violet mb-4 text-sm">
            {t("reservation.notesSection.guestNotes")}
          </h2>
          {guestNotes.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              {t("reservation.notesSection.empty")}
            </p>
          ) : (
            <div className="flex min-w-0 flex-col gap-2">
              {guestNotes.map((note, index) => (
                <div
                  key={`${index}-${note}`}
                  className="text-muted-foreground min-w-0 overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm"
                  title={note}
                >
                  <span className="inline-block min-w-max whitespace-nowrap">
                    {note}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Adult guests */}
        {adultGuests?.map(({ index: originalIndex }, idx) => (
          <React.Fragment key={originalIndex}>
            {idx % 2 ? (
              <div className="border-t border-neutral-200 md:col-span-2" />
            ) : null}

            <div
              className={cn(
                !(idx % 2) &&
                  "md:h-5 md:border-l md:border-neutral-200 md:pl-4",
              )}
            >
              <h2 className="text-violet mb-4 text-sm">
                {t("reservation.profile.adultGuest")}
              </h2>
              <div className="grid grid-cols-1 gap-x-2 gap-y-3 md:grid-cols-2">
                <FormField
                  id={`additionalGuests.${originalIndex}.firstName`}
                  label={t("reservation.form.name")}
                  type="text"
                  required
                  register={register}
                  error={
                    errors?.additionalGuests?.[originalIndex]?.firstName
                      ?.message
                  }
                  placeholder={t("reservation.placeholders.enterFirstName")}
                  isEdit={isEditing}
                  value={additionalGuests?.[originalIndex].firstName}
                  className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                  contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                  labelClassName="text-foreground"
                  containerClassName="flex flex-col space-y-3"
                />

                <FormField
                  id={`additionalGuests.${originalIndex}.lastName`}
                  label={t("reservation.form.surname")}
                  type="text"
                  required
                  register={register}
                  error={
                    errors?.additionalGuests?.[originalIndex]?.lastName?.message
                  }
                  placeholder={t("reservation.placeholders.enterSurname")}
                  isEdit={isEditing}
                  value={additionalGuests?.[originalIndex].lastName}
                  className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                  contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                  labelClassName="text-foreground"
                  containerClassName="flex flex-col space-y-3"
                />

                <FormField
                  id={`additionalGuests.${originalIndex}.phone`}
                  label={t("reservation.form.phone")}
                  type="text"
                  required
                  register={register}
                  error={
                    errors?.additionalGuests?.[originalIndex]?.phone?.message
                  }
                  onChange={onPhoneChange}
                  placeholder={t("reservation.placeholders.enterPhone")}
                  isEdit={isEditing}
                  value={additionalGuests?.[originalIndex].phone}
                  isCopy={true}
                  className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                  contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                  labelClassName="text-foreground"
                  containerClassName="flex flex-col space-y-3"
                />

                <FormField
                  id={`additionalGuests.${originalIndex}.email`}
                  label={t("reservation.form.email")}
                  type="email"
                  required
                  register={register}
                  error={
                    errors?.additionalGuests?.[originalIndex]?.email?.message
                  }
                  placeholder={t("reservation.placeholders.enterEmail")}
                  isEdit={isEditing}
                  value={additionalGuests?.[originalIndex].email}
                  isCopy={true}
                  className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                  contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                  labelClassName="text-foreground"
                  containerClassName="flex flex-col space-y-3"
                />

                <CountryField
                  className="md:col-span-1"
                  selectedCountry={
                    additionalGuests?.[originalIndex]?.language ?? ""
                  }
                  onSelect={(code) =>
                    setValue(`additionalGuests.${originalIndex}.language`, code)
                  }
                  error={
                    errors?.additionalGuests?.[originalIndex]?.language?.message
                  }
                  isEdit={isEditing}
                  labelClassName="text-foreground"
                  contentClassName={`h-9 mt-3 px-3 py-1 items-center rounded-lg !border ${errors?.additionalGuests?.[originalIndex]?.language?.message ? "!border-red-500" : "!border-neutral-200"} !text-muted-foreground !text-sm`}
                />

                <NonEditableFormField
                  value={
                    additionalGuests?.[originalIndex].dateOfBirth
                      ? format(
                          additionalGuests[originalIndex].dateOfBirth,
                          "dd.MM.yyyy",
                        )
                      : t("reservation.form.dateOfBirth")
                  }
                  label={t("reservation.form.dateOfBirth")}
                  isCopy={false}
                  labelClassName="text-foreground"
                  contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                  containerClassName="flex-1"
                />
              </div>
            </div>
          </React.Fragment>
        ))}

        {/* Child guests */}
        {childGuests?.map(({ index: originalIndex }, idx) => {
          const gridIndexOneBased = 1 + adultGuests.length + 1 + idx;
          const isRightColumnOnMd = !(gridIndexOneBased % 2);

          return (
            <React.Fragment key={originalIndex}>
              {(adultGuests.length % 2 && !(idx % 2)) ||
              (!(adultGuests.length % 2) && idx % 2) ? (
                <div className="border-t border-neutral-200 md:col-span-2" />
              ) : null}

              <div
                className={cn(
                  isRightColumnOnMd &&
                    "md:h-5 md:border-l md:border-neutral-200 md:pl-4",
                )}
              >
                <h2 className="text-violet mb-4 text-sm">
                  {t("reservation.profile.childGuest")}
                </h2>
                <div className="grid grid-cols-1 gap-x-2 gap-y-3 md:grid-cols-2">
                  <FormField
                    id={`additionalGuests.${originalIndex}.firstName`}
                    label={t("reservation.form.name")}
                    type="text"
                    required
                    register={register}
                    error={
                      errors?.additionalGuests?.[originalIndex]?.firstName
                        ?.message
                    }
                    placeholder={t("reservation.placeholders.enterFirstName")}
                    isEdit={isEditing}
                    value={additionalGuests?.[originalIndex].firstName}
                    className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                    contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                    labelClassName="text-foreground"
                    containerClassName="flex flex-col space-y-3"
                  />

                  <FormField
                    id={`additionalGuests.${originalIndex}.lastName`}
                    label={t("reservation.form.surname")}
                    type="text"
                    required
                    register={register}
                    error={
                      errors?.additionalGuests?.[originalIndex]?.lastName
                        ?.message
                    }
                    placeholder={t("reservation.placeholders.enterSurname")}
                    isEdit={isEditing}
                    value={additionalGuests?.[originalIndex].lastName}
                    className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                    contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                    labelClassName="text-foreground"
                    containerClassName="flex flex-col space-y-3"
                  />

                  <FormField
                    id={`additionalGuests.${originalIndex}.phone`}
                    label={t("reservation.form.phone")}
                    type="text"
                    required
                    register={register}
                    error={
                      errors?.additionalGuests?.[originalIndex]?.phone?.message
                    }
                    onChange={onPhoneChange}
                    placeholder={t("reservation.placeholders.enterPhone")}
                    isEdit={isEditing}
                    value={additionalGuests?.[originalIndex].phone}
                    isCopy={true}
                    className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                    contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                    labelClassName="text-foreground"
                    containerClassName="flex flex-col space-y-3"
                  />

                  <FormField
                    id={`additionalGuests.${originalIndex}.email`}
                    label={t("reservation.form.email")}
                    type="email"
                    required
                    register={register}
                    error={
                      errors?.additionalGuests?.[originalIndex]?.email?.message
                    }
                    placeholder={t("reservation.placeholders.enterEmail")}
                    isEdit={isEditing}
                    value={additionalGuests?.[originalIndex].email}
                    isCopy={true}
                    className="text-muted-foreground h-9 items-center rounded-lg border border-neutral-200 px-3 py-1 text-sm"
                    contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                    labelClassName="text-foreground"
                    containerClassName="flex flex-col space-y-3"
                  />

                  <CountryField
                    className="md:col-span-1"
                    selectedCountry={
                      additionalGuests?.[originalIndex]?.language ?? ""
                    }
                    onSelect={(code) =>
                      setValue(
                        `additionalGuests.${originalIndex}.language`,
                        code,
                      )
                    }
                    error={
                      errors?.additionalGuests?.[originalIndex]?.language
                        ?.message
                    }
                    isEdit={isEditing}
                    labelClassName="text-foreground"
                    contentClassName={`h-9 mt-3 px-3 py-1 items-center rounded-lg !border ${errors?.additionalGuests?.[originalIndex]?.language?.message ? "!border-red-500" : "!border-neutral-200"} !text-muted-foreground !text-sm`}
                  />

                  <NonEditableFormField
                    value={
                      additionalGuests?.[originalIndex].dateOfBirth
                        ? format(
                            additionalGuests[originalIndex].dateOfBirth,
                            "dd.MM.yyyy",
                          )
                        : t("reservation.form.dateOfBirth")
                    }
                    label={t("reservation.form.dateOfBirth")}
                    isCopy={false}
                    labelClassName="text-foreground"
                    contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                    containerClassName="flex-1"
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};
